/**
 * Advanced Options pane for a single conference room (Phase 12.2 of the
 * conference dashboard rollout). The form is sectioned into six groups
 * that map 1:1 to the backend's `conference_rooms_options` columns:
 *
 *   1. Entry          — mute_on_entry, wait_for_moderator, entry_chime
 *   2. Recording      — recording_mode (off / auto_on_mod_join / manual)
 *                       + a folder picker placeholder (out of scope here)
 *   3. Moderation     — qa_mode_default, moderator_dtmf_enabled,
 *                       caller_dtmf_enabled, promotion_key
 *   4. Limits         — lock_on_full, participant_limit_override
 *   5. Dial-out       — dial_out_enabled
 *   6. Q&A            — qa_play_prerecorded
 *
 * Save-on-change: every field PATCHes the backend immediately on change
 * with an optimistic update so the UI reflects the new value without a
 * refetch round-trip. The TanStack Query cache key is keyed on the room
 * id so multiple rooms in the same session don't collide.
 *
 * Folder picker is intentionally a disabled stub — the room-level
 * "recording folder" link is owned by Phase 13.x; the placeholder leaves
 * a visible anchor so the section reads complete on this page.
 *
 * RTL note: all inline spacing uses logical-aware Tailwind utilities
 * (gap-*, space-y-*, p-*) which translate cleanly under `dir="rtl"`.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { conferenceOptions } from '@/api/conferenceOptions';
import type {
  ConferenceOptions,
  PatchConferenceOptionsBody,
  RecordingMode,
} from '@/api/conferenceOptions';
import { conferenceRooms } from '@/api/conferenceRooms';
import type { ConferenceRoomRow } from '@/api/conferenceRooms';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Phone, Mic, Lock, Megaphone, Activity, MessageSquareWarning,
} from 'lucide-react';
import { RecordingPicker } from '@/components/ivr/RecordingPicker';
import { FolderPicker } from '@/components/ivr/FolderPicker';

export interface AdvancedOptionsTabProps {
  roomId: string;
  /**
   * Customer id of the room. Threaded into the wait-music
   * RecordingPicker so super_admin / reseller_owner sessions can browse
   * recordings scoped to the room's owning customer rather than their
   * own (empty) tenant. office_manager / standard_user can omit it —
   * the server falls back to their tenant.
   */
  customerId?: string | null;
}

export function AdvancedOptionsTab({ roomId, customerId }: AdvancedOptionsTabProps) {
  const qc = useQueryClient();
  const queryKey = ['conferenceOptions', roomId] as const;
  const roomKey = ['conferenceRoom', roomId] as const;

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => conferenceOptions.get(roomId),
  });

  // Recording folder lives on the room itself (conference_rooms
  // .recording_target_folder_id), NOT on conference_rooms_options. We
  // fetch the room alongside the options so the Recording section can
  // bind a single folder field without a second round-trip on edit.
  const { data: room } = useQuery({
    queryKey: roomKey,
    queryFn: () => conferenceRooms.get(roomId),
  });

  // Local mirror so optimistic updates show instantly even while a PATCH
  // is in flight. Synced from the query whenever the server snapshot
  // changes (initial load + post-PATCH success).
  const [optimistic, setOptimistic] = useState<ConferenceOptions | null>(null);
  useEffect(() => { if (data) setOptimistic(data); }, [data]);

  // Same idea for the room's folder binding. Stored separately because
  // the field lives on a different table behind a different PATCH.
  const [folderOptimistic, setFolderOptimistic] = useState<string | null>(
    room?.recording_target_folder_id ?? null,
  );
  useEffect(() => {
    if (room) setFolderOptimistic(room.recording_target_folder_id);
  }, [room]);

  const patch = useMutation({
    mutationFn: (body: PatchConferenceOptionsBody) =>
      conferenceOptions.patch(roomId, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<ConferenceOptions>(queryKey);
      if (prev) {
        const next = { ...prev, ...body };
        qc.setQueryData<ConferenceOptions>(queryKey, next);
        setOptimistic(next);
      }
      return { prev };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData<ConferenceOptions>(queryKey, ctx.prev);
        setOptimistic(ctx.prev);
      }
    },
    onSuccess: (updated) => {
      qc.setQueryData<ConferenceOptions>(queryKey, updated);
      setOptimistic(updated);
    },
  });

  const patchFolder = useMutation({
    mutationFn: (folderId: string | null) =>
      conferenceRooms.update(roomId, { recording_target_folder_id: folderId }),
    onMutate: async (folderId) => {
      await qc.cancelQueries({ queryKey: roomKey });
      const prev = qc.getQueryData<ConferenceRoomRow>(roomKey);
      if (prev) {
        const next = { ...prev, recording_target_folder_id: folderId };
        qc.setQueryData<ConferenceRoomRow>(roomKey, next);
        setFolderOptimistic(folderId);
      }
      return { prev };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData<ConferenceRoomRow>(roomKey, ctx.prev);
        setFolderOptimistic(ctx.prev.recording_target_folder_id);
      }
    },
    onSuccess: (updated) => {
      qc.setQueryData<ConferenceRoomRow>(roomKey, updated);
      setFolderOptimistic(updated.recording_target_folder_id);
    },
  });

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground" role="status">
        Loading advanced options…
      </div>
    );
  }
  if (isError || !optimistic) {
    return (
      <div className="text-sm text-destructive" role="alert">
        Failed to load advanced options
        {error instanceof Error ? `: ${error.message}` : '.'}
      </div>
    );
  }

  const opts = optimistic;
  const apply = (body: PatchConferenceOptionsBody) => patch.mutate(body);
  const applyFolder = (folderId: string | null) => patchFolder.mutate(folderId);

  return (
    <div className="space-y-4" data-testid="advanced-options-tab">
      <SectionEntry opts={opts} onChange={apply} customerId={customerId ?? null} />
      <SectionRecording
        opts={opts}
        onChange={apply}
        folderId={folderOptimistic}
        onChangeFolder={applyFolder}
        customerId={customerId ?? null}
      />
      <SectionModeration opts={opts} onChange={apply} />
      <SectionLimits opts={opts} onChange={apply} />
      <SectionDialOut opts={opts} onChange={apply} />
      <SectionQA opts={opts} onChange={apply} />
    </div>
  );
}

// ── Generic helpers ──────────────────────────────────────────────────────────

type ApplyFn = (patch: PatchConferenceOptionsBody) => void;

interface SectionProps {
  opts: ConferenceOptions;
  onChange: ApplyFn;
}

function Section({
  title, description, icon, children, testId,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <Card data-testid={testId} aria-labelledby={`${testId}-title`}>
      <CardHeader>
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
            {icon}
          </span>
          <div className="min-w-0">
            <CardTitle id={`${testId}-title`} className="text-base">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({
  id, label, hint, checked, onCheckedChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <Label htmlFor={id} className="text-sm font-medium cursor-pointer">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
      />
    </div>
  );
}

// ── Section 1: Entry ─────────────────────────────────────────────────────────

function SectionEntry({
  opts, onChange, customerId,
}: SectionProps & { customerId: string | null }) {
  return (
    <Section
      testId="section-entry"
      icon={<Phone className="h-4 w-4" />}
      title="Entry"
      description="What callers hear and how they enter the room."
    >
      <ToggleRow
        id="opt-mute-on-entry"
        label="Mute callers on entry"
        hint="New joiners arrive muted; moderators can unmute from the dashboard."
        checked={opts.mute_on_entry}
        onCheckedChange={(v) => onChange({ mute_on_entry: v })}
      />
      <ToggleRow
        id="opt-wait-for-moderator"
        label="Wait for moderator"
        hint="Callers hold with music until at least one moderator joins."
        checked={opts.wait_for_moderator}
        onCheckedChange={(v) => onChange({ wait_for_moderator: v })}
      />
      {opts.wait_for_moderator && (
        <div className="ms-6 mt-1 border-s border-border ps-4 space-y-1.5">
          <Label className="text-xs text-muted-foreground">
            Custom wait music (optional)
          </Label>
          <RecordingPicker
            value={opts.wait_music_recording_id}
            onChange={(id) => onChange({ wait_music_recording_id: id })}
            label=""
            customerId={customerId ?? undefined}
          />
          <p className="text-[11px] text-muted-foreground">
            Played to listeners while they wait for a moderator. Leave empty
            to use the platform default music.
          </p>
        </div>
      )}
      <ToggleRow
        id="opt-entry-chime"
        label="Play entry chime"
        hint="Short tone announces each join."
        checked={opts.entry_chime}
        onCheckedChange={(v) => onChange({ entry_chime: v })}
      />
    </Section>
  );
}

// ── Section 2: Recording ─────────────────────────────────────────────────────

const RECORDING_MODE_LABELS: Record<RecordingMode, string> = {
  off: 'Off — never record',
  auto_on_mod_join: 'Auto — start when first moderator joins',
  manual: 'Manual — start / stop from the live dashboard',
};

function SectionRecording({
  opts, onChange, folderId, onChangeFolder, customerId,
}: SectionProps & {
  folderId: string | null;
  onChangeFolder: (id: string | null) => void;
  customerId: string | null;
}) {
  return (
    <Section
      testId="section-recording"
      icon={<Mic className="h-4 w-4" />}
      title="Recording"
      description="When the room records itself and where the file lands."
    >
      <div className="space-y-1.5">
        <Label htmlFor="opt-recording-mode" className="text-sm font-medium">
          Recording mode
        </Label>
        <Select
          value={opts.recording_mode}
          onValueChange={(v) => onChange({ recording_mode: v as RecordingMode })}
        >
          <SelectTrigger id="opt-recording-mode" className="w-full" aria-label="Recording mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(RECORDING_MODE_LABELS) as RecordingMode[]).map((m) => (
              <SelectItem key={m} value={m}>{RECORDING_MODE_LABELS[m]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5" data-testid="recording-folder-picker">
        <Label className="text-sm font-medium">Recording folder</Label>
        <FolderPicker
          value={folderId}
          onChange={onChangeFolder}
          customerId={customerId ?? undefined}
        />
        <p className="text-xs text-muted-foreground">
          Finished mix-downs land in this folder. Leave empty to use the
          customer&rsquo;s auto-created &ldquo;Conference recordings&rdquo;
          folder.
        </p>
      </div>
    </Section>
  );
}

// ── Section 3: Moderation ────────────────────────────────────────────────────

function SectionModeration({ opts, onChange }: SectionProps) {
  // Local input mirror so the user can type without each keystroke
  // racing a PATCH. We commit on blur and on Enter.
  const [promotionDraft, setPromotionDraft] = useState(opts.promotion_key);
  const lastApplied = useRef(opts.promotion_key);
  useEffect(() => {
    if (opts.promotion_key !== lastApplied.current) {
      setPromotionDraft(opts.promotion_key);
      lastApplied.current = opts.promotion_key;
    }
  }, [opts.promotion_key]);

  function commitPromotion() {
    const trimmed = promotionDraft.trim();
    // 1–2 chars per the backend zod schema; ignore empty.
    if (trimmed.length === 0 || trimmed.length > 2) {
      setPromotionDraft(opts.promotion_key);
      return;
    }
    if (trimmed === opts.promotion_key) return;
    lastApplied.current = trimmed;
    onChange({ promotion_key: trimmed });
  }

  return (
    <Section
      testId="section-moderation"
      icon={<Megaphone className="h-4 w-4" />}
      title="Moderation"
      description="Q&A flow, who can press DTMF, and the moderator promotion key."
    >
      <ToggleRow
        id="opt-qa-mode-default"
        label="Start in Q&A mode"
        hint="Callers join muted; raise-hand queue is active by default."
        checked={opts.qa_mode_default}
        onCheckedChange={(v) => onChange({ qa_mode_default: v })}
      />
      <ToggleRow
        id="opt-moderator-dtmf-enabled"
        label="Allow moderator DTMF"
        hint="Moderators can use in-call keypad shortcuts (mute-all, lock, end)."
        checked={opts.moderator_dtmf_enabled}
        onCheckedChange={(v) => onChange({ moderator_dtmf_enabled: v })}
      />
      <ToggleRow
        id="opt-caller-dtmf-enabled"
        label="Allow caller DTMF"
        hint="Callers can raise hand, self-mute, etc. from the keypad."
        checked={opts.caller_dtmf_enabled}
        onCheckedChange={(v) => onChange({ caller_dtmf_enabled: v })}
      />
      <div className="space-y-1.5">
        <Label htmlFor="opt-promotion-key" className="text-sm font-medium">
          Moderator promotion key
        </Label>
        <Input
          id="opt-promotion-key"
          value={promotionDraft}
          maxLength={2}
          onChange={(e) => setPromotionDraft(e.target.value)}
          onBlur={commitPromotion}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          placeholder="#"
          className="font-mono w-24"
          aria-label="Moderator promotion key"
        />
        <p className="text-xs text-muted-foreground">
          One or two DTMF characters callers press after their admin PIN
          to elevate to moderator (e.g. <code>#</code> or <code>*9</code>).
        </p>
      </div>
    </Section>
  );
}

// ── Section 4: Limits ────────────────────────────────────────────────────────

function SectionLimits({ opts, onChange }: SectionProps) {
  const [override, setOverride] = useState<string>(
    opts.participant_limit_override == null ? '' : String(opts.participant_limit_override),
  );
  const lastApplied = useRef(opts.participant_limit_override);
  useEffect(() => {
    if (opts.participant_limit_override !== lastApplied.current) {
      setOverride(
        opts.participant_limit_override == null ? '' : String(opts.participant_limit_override),
      );
      lastApplied.current = opts.participant_limit_override;
    }
  }, [opts.participant_limit_override]);

  function commitOverride() {
    const trimmed = override.trim();
    if (trimmed === '') {
      if (opts.participant_limit_override == null) return;
      lastApplied.current = null;
      onChange({ participant_limit_override: null });
      return;
    }
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n < 2 || n > 500) {
      setOverride(opts.participant_limit_override == null ? '' : String(opts.participant_limit_override));
      return;
    }
    if (n === opts.participant_limit_override) return;
    lastApplied.current = n;
    onChange({ participant_limit_override: n });
  }

  return (
    <Section
      testId="section-limits"
      icon={<Lock className="h-4 w-4" />}
      title="Limits"
      description="What happens when the room fills up and per-room participant caps."
    >
      <ToggleRow
        id="opt-lock-on-full"
        label="Lock when full"
        hint="Auto-lock once participant count hits the cap; later callers are bounced."
        checked={opts.lock_on_full}
        onCheckedChange={(v) => onChange({ lock_on_full: v })}
      />
      <div className="space-y-1.5">
        <Label htmlFor="opt-participant-limit" className="text-sm font-medium">
          Participant limit override
        </Label>
        <Input
          id="opt-participant-limit"
          type="number"
          inputMode="numeric"
          min={2}
          max={500}
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          onBlur={commitOverride}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          placeholder="(use room max)"
          className="w-32"
          aria-label="Participant limit override"
        />
        <p className="text-xs text-muted-foreground">
          Leave blank to use the room&rsquo;s <code>max_participants</code>{' '}
          value. Set a number (2–500) to cap this room independently.
        </p>
      </div>
    </Section>
  );
}

// ── Section 5: Dial-out ──────────────────────────────────────────────────────

function SectionDialOut({ opts, onChange }: SectionProps) {
  return (
    <Section
      testId="section-dial-out"
      icon={<Activity className="h-4 w-4" />}
      title="Dial-out"
      description="Whether moderators can dial third parties into the live room."
    >
      <ToggleRow
        id="opt-dial-out-enabled"
        label="Allow dial-out"
        hint="Moderators can place outbound legs (PSTN or SIP) from the live dashboard."
        checked={opts.dial_out_enabled}
        onCheckedChange={(v) => onChange({ dial_out_enabled: v })}
      />
    </Section>
  );
}

// ── Section 6: Q&A ───────────────────────────────────────────────────────────

function SectionQA({ opts, onChange }: SectionProps) {
  return (
    <Section
      testId="section-qa"
      icon={<MessageSquareWarning className="h-4 w-4" />}
      title="Q&A"
      description="Pre-recorded clip behaviour while a hand is raised."
    >
      <ToggleRow
        id="opt-qa-play-prerecorded"
        label="Play pre-recorded waiting clip"
        hint="Plays a custom hold message to the queue while waiting to be called on."
        checked={opts.qa_play_prerecorded}
        onCheckedChange={(v) => onChange({ qa_play_prerecorded: v })}
      />
    </Section>
  );
}
